import { ActivityPanel } from './ActivityPanel';
import { N8nDomainsPanel } from './N8nDomainsPanel';
import { AuditLogPanel } from './AuditLogPanel';
import { DatabaseStoragePanel } from './DatabaseStoragePanel';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Switch,
  Tab,
  Tabs,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { SECTION_CATALOG, type SectionId } from '../api/generated/section-catalog';
import { PageHeader } from '../components/PageHeader';
import { Box, Stack, Typography } from '../components/mui-system';
import { useSections } from '../section-context';
import { AccessPage } from './AccessPage';

export function AdminPage() {
  const { tab = 'sections' } = useParams();
  if (
    tab !== 'sections' &&
    tab !== 'users' &&
    tab !== 'audit' &&
    tab !== 'activity' &&
    tab !== 'n8n-domains' &&
    tab !== 'database'
  )
    return <Navigate to="/admin/sections" replace />;
  return (
    <Box>
      <PageHeader
        title="Администрирование"
        description="Разделы приложения, доступ пользователей, активность, журнал действий и объём базы."
      />
      <Tabs
        value={tab}
        aria-label="Администрирование"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        <Tab value="sections" label="Разделы" component={Link} to="/admin/sections" />
        <Tab value="users" label="Пользователи" component={Link} to="/admin/users" />
        <Tab value="activity" label="Активность" component={Link} to="/admin/activity" />
        <Tab value="audit" label="Журнал действий" component={Link} to="/admin/audit" />
        <Tab value="database" label="База данных" component={Link} to="/admin/database" />
        <Tab value="n8n-domains" label="Наши домены n8n" component={Link} to="/admin/n8n-domains" />
      </Tabs>
      {tab === 'sections' && <SectionVisibilityPanel />}
      {tab === 'users' && <AccessPage embedded />}
      {tab === 'activity' && <ActivityPanel />}
      {tab === 'audit' && <AuditLogPanel />}
      {tab === 'database' && <DatabaseStoragePanel />}
      {tab === 'n8n-domains' && <N8nDomainsPanel />}
    </Box>
  );
}

function SectionVisibilityPanel() {
  const { sections, update, refresh } = useSections();
  const [pending, setPending] = useState<SectionId[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function toggle(id: SectionId, visible: boolean) {
    setPending((rows) => [...rows, id]);
    setError('');
    setNotice('');
    try {
      await update(id, visible);
      const label = SECTION_CATALOG.find((section) => section.id === id)!.label;
      setNotice(`Раздел «${label}» ${visible ? 'показан' : 'скрыт'} для всех пользователей.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Не удалось сохранить видимость раздела');
    } finally {
      setPending((rows) => rows.filter((item) => item !== id));
    }
  }

  return (
    <Card sx={{ maxWidth: 820 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h3" mb={1}>
          Видимость разделов
        </Typography>
        <Typography color="text.secondary" mb={2}>
          Выберите разделы, которые будут видны всем пользователям во всех воркспейсах. Изменения
          сохраняются сразу. Данные скрытых разделов сохраняются.
        </Typography>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" onClick={() => void refresh()}>
                Обновить
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
            {notice}
          </Alert>
        )}
        <Stack divider={<Divider />}>
          {SECTION_CATALOG.map((section) => {
            const setting = sections.find((row) => row.id === section.id)!;
            const saving = pending.includes(section.id);
            return (
              <Stack key={section.id} direction="row" alignItems="center" gap={1.5} py={1.6}>
                <Icon icon={section.icon} width={24} />
                <Box flex={1}>
                  <Typography fontWeight={700}>{section.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {saving ? 'Сохраняем…' : setting.visible ? 'Показан' : 'Скрыт'}
                  </Typography>
                </Box>
                {saving && <CircularProgress size={18} />}
                <Switch
                  checked={setting.visible}
                  disabled={saving}
                  onChange={(_, checked) => void toggle(section.id, checked)}
                  slotProps={{ input: { 'aria-label': `Показывать раздел «${section.label}»` } }}
                />
              </Stack>
            );
          })}
        </Stack>
        <Typography variant="body2" color="text.secondary" mt={2}>
          Админка всегда доступна администраторам, чтобы можно было вернуть скрытые разделы.
        </Typography>
      </CardContent>
    </Card>
  );
}
